import { useEffect, useMemo, useCallback, useRef } from "react";

export const useSelectBase = ({
  formData,
  fieldId,
  setFormData,
  rawData,
  rawOptions,
  mapToOption,
  isMulti,
  defaultValue,
}) => {
  // Cache para manter a mesma referência de opção para cada item cru
  const optionCacheRef = useRef(new WeakMap());

  const getNestedValue = (obj, path) => {
    if (!obj || !path || typeof path !== "string") return undefined;
    const parts = path.split(".");
    return parts.reduce((acc, part) => (acc ? acc[part] : undefined), obj);
  };

  // baseOptions agora é estável: reutiliza opções já criadas
  const baseOptions = useMemo(() => {
    if (!Array.isArray(rawOptions)) return [];

    return rawOptions.reduce((acc, item) => {
      if (!item) return acc;

      // Verifica se já existe uma opção para este item
      let opt = optionCacheRef.current.get(item);

      if (!opt) {
        // Cria a opção apenas na primeira vez
        opt = mapToOption(item, rawData);
        optionCacheRef.current.set(item, opt);
      }

      // Valida a opção
      if (
        opt &&
        opt.value !== undefined &&
        opt.label !== undefined &&
        !String(opt.label).includes("undefined")
      ) {
        acc.push(opt);
      }

      return acc;
    }, []);
  }, [rawOptions, mapToOption, rawData]);

  // Simplifica a normalização: busca nas opções estáveis
  const normalizeValueToOption = useCallback(
    (value) => {
      if (value == null) return null;

      // Se já é um objeto de opção, tenta encontrar a versão estável
      if (typeof value === "object" && "value" in value) {
        return (
          baseOptions.find((opt) => opt.value === value.value) || value
        );
      }

      // Se é um valor primitivo (ex: id)
      return baseOptions.find((opt) => opt.value === value) || null;
    },
    [baseOptions]
  );

  const selectedValue = useMemo(() => {
    if (!formData || !fieldId) {
      return isMulti ? [] : null;
    }

    const value = getNestedValue(formData, fieldId);

    if (isMulti) {
      if (!Array.isArray(value)) return [];
      return value
        .map((v) => normalizeValueToOption(v))
        .filter(Boolean);
    }

    return normalizeValueToOption(value);
  }, [
    formData,
    fieldId,
    isMulti,
    normalizeValueToOption,
  ]);

  const options = useMemo(() => {
    if (isMulti) {
      // Garante que os selecionados estejam presentes mesmo se não estiverem nas opções
      const extras = selectedValue.filter(
        (sel) =>
          sel?.value && !baseOptions.some((opt) => opt.value === sel.value)
      );
      return [...baseOptions, ...extras];
    }
    return baseOptions;
  }, [baseOptions, selectedValue, isMulti]);

  const reset = useCallback(() => {
    if (!setFormData || !fieldId) return
    setFormData((prev) => {
      const parts = fieldId.split(".")
      const newData = { ...prev }
      let current = newData
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {}
        current = current[parts[i]]
      }
      current[parts[parts.length - 1]] = isMulti ? [] : null
      return newData
    })
  }, [setFormData, fieldId, isMulti])

  useEffect(() => {
    if (!setFormData || !fieldId || defaultValue == null) return;
    const currentValue = getNestedValue(formData, fieldId);
    const isEmpty = isMulti
      ? !Array.isArray(currentValue) || currentValue.length === 0
      : currentValue == null || currentValue === "";
    if (!isEmpty) return;
    setFormData((prev) => {
      const parts = fieldId.split(".");
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = defaultValue;
      return newData;
    });
  }, [fieldId, defaultValue, setFormData, formData, isMulti]);

  return { options, selectedValue, reset, rawData: rawOptions };
};